import { createSelector } from 'reselect';
import { getPrefixedWizardSessionId } from '../../lib/wizardSession';
import { getBusinessInfo } from '../businessInfo/selectors';
import { IBusinessInfo } from '../businessInfo/types';
import { getSelectedMediaURLs } from '../media/selectors';
import { SelectorWithProps } from '../rootReducer';
import { isShortFlowActive } from '../videoStyle/selectors';
import { IVideoStyle, IVideoStyleOptions } from '../videoStyle/types';
import { getVideoText } from '../videoText/selectors';

import {
  ICreateVideoRequest,
  normalizeMediaSources,
  VideoScripts,
} from '../../api/videoMaker';

const prepareBusinessData = (
  businessInfo: IBusinessInfo,
  styles: IVideoStyle,
  isShortFlow: boolean,
) => {
  const videoStyles = {
    primaryColor: styles.primaryColor,
    secondaryColor: styles.secondaryColor,
    font: styles.activeFont,
  };

  if (!businessInfo.showClosingScene) {
    return videoStyles;
  }

  const businessData = {
    name: businessInfo.businessName,
    websiteOrEmail: businessInfo.website,
    tagline: businessInfo.businessTagline,
    phoneNumber: businessInfo.phoneNumber,
    extraInfo: businessInfo.address,
    logoUrl: businessInfo.logo,
  };

  // NOTE: for short flow skip business section if only business name provided
  // https://jira.wixpress.com/browse/VMAF-289
  if (
    isShortFlow &&
    !Object.keys(businessData).some(
      (key) => key !== 'name' && !!businessData[key],
    )
  ) {
    return videoStyles;
  }

  return {
    ...businessData,
    ...videoStyles,
    show_logo: false,
  };
};

interface IPrepareVideoInputProps {
  videoStyleOptions: IVideoStyleOptions;
}

const prepareVideoInput: SelectorWithProps<
  ICreateVideoRequest,
  IPrepareVideoInputProps
> = createSelector(
  isShortFlowActive,
  (state, props: IPrepareVideoInputProps) => props.videoStyleOptions,
  getBusinessInfo,
  getSelectedMediaURLs,
  getVideoText,
  (
    isShortFlow,
    videoStyleOptions,
    businessInfo,
    media,
    { openingText, mainMessage, closingText },
  ) => ({
    apiClientId: 'vmaf',
    userSessionId: getPrefixedWizardSessionId(),
    script: VideoScripts.GENERAL as VideoScripts.GENERAL,
    business: prepareBusinessData(
      businessInfo,
      videoStyleOptions.style,
      isShortFlow,
    ),
    scriptData: {
      openingText,
      mainMessage,
      closingText,
    },
    sources: normalizeMediaSources(media),
    trackId: videoStyleOptions.musicTrackId,
    themeId: videoStyleOptions.themeId,
    // TODO: Remove this after fix from BE side
    siteId: '2a6497dc-f49c-484d-ad7c-fb777417dbcb',
  }),
);

export { prepareVideoInput };
